import { useCallback, useLayoutEffect, useRef } from "react";

/** Поэлементное равенство списков строк по значению. */
export function sameStringList(left: readonly string[], right: readonly string[]): boolean {
  return samePrimitiveList(left, right);
}

/** Поэлементное равенство списков чисел по значению. */
export function sameNumberList(left: readonly number[], right: readonly number[]): boolean {
  return samePrimitiveList(left, right);
}

function samePrimitiveList<T extends string | number>(
  left: readonly T[],
  right: readonly T[],
): boolean {
  if (left === right) {
    return true;
  }
  if (left.length !== right.length) {
    return false;
  }
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) {
      return false;
    }
  }
  return true;
}

/** Равенство словарей «ключ → число» по значению. */
export function sameCountRecord(
  left: Readonly<Record<string, number | undefined>>,
  right: Readonly<Record<string, number | undefined>>,
): boolean {
  if (left === right) {
    return true;
  }
  const leftKeys = Object.keys(left);
  if (leftKeys.length !== Object.keys(right).length) {
    return false;
  }
  return leftKeys.every((key) => left[key] === right[key]);
}

/**
 * Возвращает прежнюю ссылку, пока новое значение равно прежнему по `isEqual`.
 * Нужен производным массивам и словарям, которые пересчитываются на каждый
 * кадр потока, но по содержимому почти не меняются: мемоизированный потомок
 * иначе перерисовывался бы из-за новой ссылки на тот же состав.
 */
export function useStableValue<T>(next: T, isEqual: (previous: T, next: T) => boolean): T {
  const ref = useRef(next);
  if (ref.current !== next && !isEqual(ref.current, next)) {
    ref.current = next;
  }
  return ref.current;
}

/**
 * Колбэк с постоянной ссылкой, всегда вызывающий актуальную версию функции.
 * Обработчики экрана зависят от состояния, которое меняется на каждый кадр
 * потока (сопоставления закреплений, ожидающие запросы), а мемоизированные
 * строки не вправе перерисовываться из-за новой ссылки. Вызывать можно только
 * из обработчиков событий, не во время рендера.
 */
export function useStableCallback<Args extends unknown[], Result>(
  callback: (...args: Args) => Result,
): (...args: Args) => Result {
  const ref = useRef(callback);
  useLayoutEffect(() => {
    ref.current = callback;
  });
  return useCallback((...args: Args) => ref.current(...args), []);
}
